import { ReactNode, useCallback, MouseEvent, ComponentType, useState } from "react";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { Panel, usePanelLevel } from "../Panel";
import * as Color from "../Color";
import { Checkbox, CheckboxValue } from "capitalrx-components-2.0/lib/forms/Checkbox";
import { Shelf, Stack, StackProps } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import { Icon } from "../Icon";
import styled from "styled-components";
import classNames from "classnames";

/** The caret type. */
export type CaretType = "icon" | "checkbox";

/** The caret alignment. */
export type CaretAlignment = "left" | "right";

/** The set of available collapse appearances. */
export type CollapseAppearance = "contained" | "underlined" | "underlined-no-indent" | "blank" | "blank-no-indent" | "checkbox";

/** A toggle expand event handler function. */
export interface ToggleExpandHandler {
  /**
   * Called when the expanded state of the collapse is to be toggled.
   * @param expanded The expanded state to move to.
   * @param previous The previous/current expanded state.
   */
  (expanded: boolean, previous: boolean): void;
}

/**
 * Uses simple in-memory collapse state management, providing the expansion state and expand state toggle handler.
 * @param initialExpanded The optional initial collapse expansion value.
 * @returns A tuple containing the current expansion state and expand state toggle handler function.
 */
export function useSimpleCollapseState(initialExpanded?: boolean): [boolean | undefined, ToggleExpandHandler] {
  const [expanded, setExpanded] = useState<boolean | undefined>(initialExpanded);

  const expandToggleHandler = useCallback<ToggleExpandHandler>((next) => {
    setExpanded(next);
  }, []);

  return [expanded, expandToggleHandler];
}

/** The collapse element. */
const CollapseElement = styled(Stack)`

`;

interface HeaderButtonProps {
  expanded?: boolean;
  showBorderBottom?: boolean;
  unpadded?: boolean;
}

/* The header button element. */
const HeaderButtonElement = styled.button<HeaderButtonProps>`
  width: 100%;
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  padding-left: ${({ unpadded }) => (unpadded ? "0" : "1.5rem")}; // <-- Conditional left padding
  margin: 0;
  text-align: left;
  appearance: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  border-bottom: ${({ expanded, showBorderBottom }) =>
    expanded && showBorderBottom ? `solid 1px ${Color.toString(Color.LightGrey["200"])}` : "none"};

  &:hover,
  &:focus {
    outline: none;
    --title-color: ${Color.toString(Color.Turqoise["400"])};
  }
`;

/** The header element. */
const HeaderElement = styled(Shelf)`
  transition: none;
`;

/** The header content element. */
const HeaderContentElement = styled.div`
  flex: 1;
`;

/** The caret element. */
const CaretElement = styled(Icon)`
  &>.icon-container {
    path, circle, g {
      transition: fill ease 0.2s;
      fill: var(--title-color, ${Color.toString(Color.DarkGrey["500"])});
    }
  }
`;

/** The title element. */
const TitleElement = styled(Semibold)`
  transition: color ease 0.2s;
  display: block;
  color: var(--title-color, ${Color.toString(Color.DarkGrey["500"])});
`;

/** The content container element. */
const ContentContainerElement = styled.div`

`;

/** The `Collapse` header component properties. */
export interface CollapseHeaderProps {

  /** The header title. */
  readonly title: ReactNode;

  /** Indicates whether the collapse is expanded or not. */
  readonly expanded: boolean;

  /** Indicates which side to align the caret. */
  readonly alignCaret: CaretAlignment;

  /** Called when the collapse expand state is to be toggled. */
  readonly onToggleExpand: ToggleExpandHandler;

  /** Aria id for the header element. */
  readonly headerId?: string;

  /** Aria id for the content element. */
  readonly contentId?: string;
}

/** The `Collapse` content component properties. */
export interface CollapseContentProps {

  /** The content class name. */
  readonly className: string;

  /** Indicates whether the collapse is expanded or not. */
  readonly expanded: boolean;

  /** The component children. */
  readonly children?: ReactNode;
}

/** The `CustomCollapse` component properties. */
export interface CustomCollapseProps extends BaseProps {

  /** The collapse title. */
  readonly title?: ReactNode;

  /** The collapse child content. */
  readonly children?: ReactNode;

  /** The component to use to render the collapse header. If not specified a default is used. */
  readonly headerComponent: ComponentType<CollapseHeaderProps>;

  /** The component to use to render the collapse content. If not specified a default is used. */
  readonly contentComponent: ComponentType<CollapseContentProps>;

  /**
   * Indicates which side to align the caret. If not specified defaults to `'right'`.
   * NOTE: Only applies if `appearance` is not `"checkbox"`.
   */
  readonly alignCaret?: CaretAlignment;

  /** Indicates whether the collapse is expanded. If not specified defaults to `false`. */
  readonly expanded?: boolean;

  /** Indicates whether a closed collapse keeps content in the DOM. If not specified defaults to `false`. */
  readonly keepHiddenContentInDOM?: boolean;

  /** Called when the collapse expand state is to be toggled. */
  readonly onToggleExpand?: ToggleExpandHandler;
}

// TODO: For now this is internally called InternalCollapse and wrapped by the original Collapse, but we will eventually transition this to the actual component.

/** A component which collapses and expands its child content. */
const CustomCollapse = (props: CustomCollapseProps) => {
  const {
    expanded = false,
    headerComponent: HeaderComponent,
    contentComponent: ContentComponent,
    alignCaret = "right",
    title,
    children,
    keepHiddenContentInDOM = false,
    onToggleExpand,
  } = props;

  const safeTitle = typeof title === "string" ? title : "collapse";
  const contentId = `collapse-content-${safeTitle.replace(/\s+/g, "-").toLowerCase()}`;
  const headerId = `collapse-header-${safeTitle.replace(/\s+/g, "-").toLowerCase()}`;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: ["collapse", { "collapsed": !expanded, expanded }],
    testIdPrefix: "collapse",
  });

  const toggleExpand = useCallback((expanded: boolean, previous: boolean) => {
    onToggleExpand?.(expanded, previous);
  }, [onToggleExpand]);

  return (
    <CollapseElement {...getBasePropsFromHtmlProps(baseHtmlProps)} spacing="none">
      <HeaderComponent
        title={title}
        expanded={expanded}
        alignCaret={alignCaret}
        onToggleExpand={toggleExpand}
        headerId={headerId}
        contentId={contentId}
      />
      <ContentComponent className="content" expanded={expanded}>
        <ContentContainerElement
          className="content-container"
          id={contentId}
          role="region"
          aria-labelledby={headerId}
          hidden={!expanded}
        >
          {(keepHiddenContentInDOM || expanded) && children}
        </ContentContainerElement>
      </ContentComponent>
    </CollapseElement>
  );
};

/** The common icon collapse header. */
const CommonIconCollapseHeader = (props: CollapseHeaderProps & { showBorderBottom?: boolean; unpadded?: boolean }) => {
  const { title, expanded, alignCaret, onToggleExpand, showBorderBottom = true, unpadded = false } = props;
  const safeTitle = typeof title === "string" ? title : "collapse";
  const contentId = `collapse-content-${safeTitle.replace(/\s+/g, "-").toLowerCase()}`;
  const headerId = `collapse-header-${safeTitle.replace(/\s+/g, "-").toLowerCase()}`;

  const headerClickHandler = useCallback((ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    onToggleExpand?.(!expanded, expanded);
  }, [expanded, onToggleExpand]);

  return (
    <HeaderButtonElement
      id={headerId}
      type="button"
      aria-expanded={expanded}
      aria-controls={contentId}
      onClick={headerClickHandler}
      className="collapse-header-btn"
      expanded={expanded}
      showBorderBottom={showBorderBottom}
      unpadded={unpadded}
    >
      <HeaderElement className="header" spacing="mini">
        {alignCaret === "left" && (
          <CaretElement name={expanded ? "chevron-down" : "chevron-right"} size="regular" />
        )}
        <HeaderContentElement as={Body}>
          <TitleElement className="title">{title}</TitleElement>
        </HeaderContentElement>
        {alignCaret === "right" && (
          <CaretElement name={expanded ? "chevron-up" : "chevron-down"} size="regular" />
        )}
      </HeaderElement>
    </HeaderButtonElement>
  );
};

/** The common checkbox collapse header. */
const CommonCheckboxCollapseHeader = (props: CollapseHeaderProps) => {
  // TODO: Add support for right alignment of the checkbox when the caret alignment is set that way.

  const { title, expanded, onToggleExpand } = props;

  const checkboxChangeHandler = useCallback((value: CheckboxValue, previousValue: CheckboxValue) => {
    if (value === "indeterminate" || value === null) return;
    if (previousValue === "indeterminate" || previousValue === null) return;

    onToggleExpand?.(value, previousValue);
  }, [onToggleExpand]);

  return (
    <HeaderContentElement className="header">
      <Checkbox value={expanded} hideInstructions hideLabel label={title} onChange={checkboxChangeHandler} />
    </HeaderContentElement>
  );
};

/** The common content component. */
const CommonContentComponent = styled(Panel) <CollapseContentProps>`
  padding: 0;
  border: none;
  background-color: unset;
  border-radius: 0;
  box-shadow: unset;
  overflow: hidden;
  max-height: ${({ expanded }) => (expanded) ? "fit-content" : "0px"};
  height: auto;
  transition: max-height 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;


/** The collapse properties for variants. */
export interface VariantCollapseProps extends Omit<CustomCollapseProps, "headerComponent" | "contentComponent"> {

  /** The caret type. If not specified defaults to `"icon"`. */
  readonly caret?: CaretType;
}

/** The collapse variant component type. */
export type CollapseVariantComponentType<P extends VariantCollapseProps> = ComponentType<P> & {

  /** The container for the `CollapseGroup` hosting the variant. */
  CollapseGroupContainer?: ComponentType<StackProps>;
};

// #region <Collapse />

/** The `Collapse` component properties. */
export interface CollapseProps extends Omit<CustomCollapseProps, "headerComponent" | "contentComponent"> {

  /**
   * Specifies the appearance of the collapse. If not specified defaults to `'contained'`.
   * @deprecated This property should no longer be used. Use the `<Collapse.Contained />`, `<Collapse.Underlined />` or `<Collapse.Blank />` components instead.
   */
  readonly appearance?: CollapseAppearance;
}

/** A component which collapses and expands its child content. */
export const Collapse = (props: CollapseProps) => {
  /*
   TODO: all of this is for backwards compatibility while we attempt to transition away from using the
   `appearance` property on the collapse and on to using styled component variants, at which point
   `Collapse` will just map directly to `Collapse.Contained`.
  */
  const { appearance = "contained", children, ...rest } = props;

  switch (appearance) {
    case "underlined":
      return (<Collapse.Underlined {...rest} caret="icon">{children}</Collapse.Underlined>);
    case "underlined-no-indent":
      return (<Collapse.Underlined.Unpadded {...rest} caret="icon">{children}</Collapse.Underlined.Unpadded>);
    case "blank":
      return (<Collapse.Blank {...rest} caret="icon">{children}</Collapse.Blank>);
    case "blank-no-indent":
      return (<Collapse.Blank.Unpadded {...rest} caret="icon">{children}</Collapse.Blank.Unpadded>);
    case "checkbox":
      return (<Collapse.Contained {...rest} caret="checkbox">{children}</Collapse.Contained>);
    case "contained":
    default:
      return (<Collapse.Contained {...rest} caret="icon">{children}</Collapse.Contained>);
  }
};

// #endregion

// #region <Collapse.Contained />

/** The `Collapse.Contained` styling for `CustomCollapse`. */
const ContainedCollapse = styled(CustomCollapse)`
  background-color: ${Color.toString(Color.White)};
  border: solid 1px ${Color.toString(Color.LightGrey["200"])};
  border-radius: 0.25rem;

  &.grouped.no-spacing {
    &:not(:last-child) {
      border-bottom:none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:not(:first-child) {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  &>.header,
  &>div>.header {
    border-bottom: ${({ expanded }) => (expanded) ? `solid 1px ${Color.toString(Color.LightGrey["200"])}` : "none"};
  }

  &>div>.header {
    padding: 1rem 1.5rem;
  }

  &>.header {
    padding: calc(1rem - 0.5rem) 1.5rem;
  }
`;

/** The `Collapse.Contained` content component. */
const ContainedContentComponent = styled(CommonContentComponent)`
  &>.content-container {
    padding: 1rem 1.5rem;
  }
`;

/** A contained variant of the `Collapse` component. */
Collapse.Contained = ((props) => {
  const { caret, children, ...rest } = props;

  const header = (caret === "checkbox") ? CommonCheckboxCollapseHeader : CommonIconCollapseHeader;

  return (
    <ContainedCollapse {...rest} headerComponent={header} contentComponent={ContainedContentComponent}>
      {children}
    </ContainedCollapse>
  );
}) as CollapseVariantComponentType<VariantCollapseProps>;

// #endregion

// #region <Collapse.Underlined />

/** The `Collapse.Underlined` styling for `CustomCollapse`. */
const UnderlinedCollapse = styled(CustomCollapse)`
  background-color: ${Color.toString(Color.White)};

  &>.header {
    padding: 1rem 1.5rem;
    padding: calc(1rem - 0.5rem) 1.5rem;
  }
`;

/** The `Collapse.Underlined` content component. */
const UnderlinedContentComponent = styled(CommonContentComponent)`
  border-bottom: solid 1px ${Color.toString(Color.LightGrey["200"])};

  &>.content-container {
    padding: 1rem 1.5rem;
  }
`;

/** An underlined variant of the `Collapse` component. */
const Underlined = ((props) => {
  const { caret, children, ...rest } = props;

  const header = (caret === "checkbox")
    ? CommonCheckboxCollapseHeader
    : (headerProps: CollapseHeaderProps) => <CommonIconCollapseHeader {...headerProps} showBorderBottom={false} />;

  return (
    <UnderlinedCollapse {...rest} headerComponent={header} contentComponent={UnderlinedContentComponent}>
      {children}
    </UnderlinedCollapse>
  );
}) as CollapseVariantComponentType<VariantCollapseProps> & {

  /** An unpadded variant of the `Collapse.Underlined` component. */
  Unpadded: CollapseVariantComponentType<VariantCollapseProps>;
};

/** The `Collapse.Underlined.Unpadded` styling for `UnderlinedCollapse`. */
const UnpaddedUnderlinedCollapse = styled(UnderlinedCollapse)`
  &>div>.header {
    padding: 1rem 0;
  }

  &>.header {
    padding: calc(1rem - 0.5rem) 0;
  }
`;

/** The `Collapse.Underlined.Unpadded` content component. */
const UnpaddedUnderlinedContentComponent = styled(UnderlinedContentComponent)`
  &>.content-container {
    padding: 1rem 0;
  }
`;

/** An unpadded variant of the `Collapse.Underlined` component. */
Underlined.Unpadded = (props) => {
  const { caret, children, ...rest } = props;

  const header = (caret === "checkbox")
    ? CommonCheckboxCollapseHeader
    : (headerProps: CollapseHeaderProps) => <CommonIconCollapseHeader {...headerProps} showBorderBottom={false} unpadded />;

  return (
    <UnpaddedUnderlinedCollapse {...rest} headerComponent={header} contentComponent={UnpaddedUnderlinedContentComponent}>
      {children}
    </UnpaddedUnderlinedCollapse>
  );
};

/** An underlined variant of the `Collapse` component. */
Collapse.Underlined = Underlined;

// #endregion

// #region <Collapse.Blank />

/** The `Collapse.Blank` styling for `CustomCollapse`. */
const BlankCollapse = styled(CustomCollapse)`
  background-color: ${Color.toString(Color.White)};

  &>div>.header {
    padding: 1rem 1.5rem;
  }

  &>.header {
    padding: calc(1rem - 0.5rem) 1.5rem;
  }
`;

/** The `Collapse.Blank` content component. */
const BlankContentComponent = styled(CommonContentComponent)`
  border-bottom: solid 1px transparent;

  &>.content-container {
    padding: 1rem 1.5rem;
  }
`;

/** A blank variant of the `Collapse` component. */
const Blank = ((props) => {
  const { caret, children, ...rest } = props;

  const header = (caret === "checkbox")
    ? CommonCheckboxCollapseHeader
    : (headerProps: CollapseHeaderProps) => <CommonIconCollapseHeader {...headerProps} showBorderBottom={false} />;

  return (
    <BlankCollapse {...rest} headerComponent={header} contentComponent={BlankContentComponent}>
      {children}
    </BlankCollapse>
  );
}) as CollapseVariantComponentType<VariantCollapseProps> & {

  /** An unpadded variant of the `Collapse.Underlined` component. */
  Unpadded: CollapseVariantComponentType<VariantCollapseProps>;
};

/** The `Collapse.Blank.Unpadded` styling for `BlankCollapse`. */
const UnpaddedBlankCollapse = styled(BlankCollapse)`
  &>div>.header {
    padding: 1rem 0;
  }

  &>.header {
    padding: calc(1rem - 0.5rem) 0;
  }
`;

/** The `Collapse.Blank.Unpadded` content component. */
const UnpaddedBlankContentComponent = styled(BlankContentComponent)`
  &>.content-container {
    padding: 1rem 0;
  }
`;

/** An unpadded variant of the `Collapse.Blank` component. */
Blank.Unpadded = (props) => {
  const { caret, children, ...rest } = props;

  const header = (caret === "checkbox")
    ? CommonCheckboxCollapseHeader
    : (headerProps: CollapseHeaderProps) => <CommonIconCollapseHeader {...headerProps} showBorderBottom={false} unpadded />;

  return (
    <UnpaddedBlankCollapse {...rest} headerComponent={header} contentComponent={UnpaddedBlankContentComponent}>
      {children}
    </UnpaddedBlankCollapse>
  );
};

/** A blank variant of the `Collapse` component. */
Collapse.Blank = Blank;

// #endregion

// #region <Collapse.Panel />

/** The root element for the `Collapse.Panel` */
const PanelCollapsePanel = styled(Panel)`
  padding: 0;

  &.grouped.no-spacing {
    box-shadow: none;

    &:not(:last-child) {
      border-bottom:none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:not(:first-child) {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }
`;

/** The `Collapse.Panel` content component. */
const PanelCollapseContentComponent = styled.div<CollapseContentProps>`
  overflow: hidden;
  max-height: ${({ expanded }) => (expanded) ? "fit-content" : "0px"};
  height: auto;
  transition: max-height 320ms cubic-bezier(0.4, 0, 0.2, 1);

  &>.content-container {
    padding: 1rem 1.5rem;
  }
`;

/** The `Collapse.Panel` styling for `CustomCollapse`. */
const PanelCollapse = styled(CustomCollapse)`
  &>.header,
  &>div>.header {
    border-bottom: ${({ expanded }) => (expanded) ? `solid 1px ${Color.toString(Color.LightGrey["200"])}` : "none"};
  }

  &>div>.header {
    padding: 1rem 1.5rem;
  }

  &>.header {
    padding: calc(1rem - 0.5rem) 1.5rem;
  }
`;

/** A panel variant of the `Collapse` component. */
const CollapsePanel = ((props) => {
  const { className, caret, children, ...rest } = props;

  const header = (caret === "checkbox") ? CommonCheckboxCollapseHeader : CommonIconCollapseHeader;

  return (
    <PanelCollapsePanel className={classNames(className)}>
      <PanelCollapse
        {...rest}
        headerComponent={header}
        contentComponent={PanelCollapseContentComponent}>{children}</PanelCollapse>
    </PanelCollapsePanel>
  );
}) as CollapseVariantComponentType<VariantCollapseProps>;

/** The collapse panel collapse group container. */
const CollapsePanelCollapseGroupContainer = styled(Stack) <{ $parentPanelLevel: ReturnType<typeof usePanelLevel> }>`
  border-radius: 0.25rem;
  box-shadow: ${({ spacing, $parentPanelLevel }) => {
    if (spacing !== "none") return "unset";
    if ($parentPanelLevel !== "none") return "unset";

    return "0 0 0.25rem rgba(0, 0, 0, 0.08)";
  }};
`;

/** The container to use for a CollapseGroup when  for the collapse group */
CollapsePanel.CollapseGroupContainer = (props) => {
  const { children, ...rest } = props;

  const parentPanelLevel = usePanelLevel();

  // style appropriately.

  return (
    <CollapsePanelCollapseGroupContainer {...rest} $parentPanelLevel={parentPanelLevel}>
      {children}
    </CollapsePanelCollapseGroupContainer>
  );
};

/** A panel variant of the `Collapse` component. */
Collapse.Panel = CollapsePanel;

// #endregion

/** A customizable version of the Collapse (will eventually also be the same as Collapse). */
Collapse.Custom = CustomCollapse;
